import { useMemo } from "react";
import { Link } from "react-router";

import { useSourceFormFields } from "@prequel/import-react";
import {
  Alert,
  AlertKind,
  DetailCardSection,
  DetailList,
  DetailRow,
  formatDateTime,
  IdValue,
  Spinner,
} from "@prequel/shared-components";

import { useAllDatasets } from "@/api/import/datasets";
import FormFieldDetails from "@/components/import/FormFieldDetails";
import PipelineCards, { type PipelineCard } from "@/components/import/PipelineCards";
import ProviderCard from "@/components/import/ProviderCard";
import { getDatasetMethodSummary } from "@/pages/import/DatasetsPage/helpers";
import { sourceFormValues } from "@/pages/import/SourcesPage/sourceFormValues";
import { useSourceContext } from "@/pages/import/SourcesPage/SourcePage";
import { getVendorLabel } from "@/pages/import/vendors";

const SourceOverview = () => {
  const { source } = useSourceContext();
  const { items: datasets, isLoading: isLoadingDatasets, error: datasetsError } = useAllDatasets(source.id);
  const initialSource = useMemo(() => sourceFormValues(source), [source]);
  const configForm = useSourceFormFields({
    providerId: source.provider_id,
    vendor: source.vendor,
    mode: "edit",
    initialSource,
  });

  const vendorLabel = getVendorLabel(source.vendor);

  const cards: PipelineCard[] = [
    {
      id: "source",
      label: "Source",
      title: source.name,
      subtitle: vendorLabel,
    },
    {
      id: "datasets",
      label: "Datasets",
      items: datasets.map((dataset) => ({
        id: dataset.id,
        title: dataset.name,
        subtitle: getDatasetMethodSummary(dataset),
        to: `/import/datasets/${dataset.id}`,
      })),
      // An empty list and a failed one look the same through `items`, so only invite a dataset
      // once the request has actually come back empty.
      emptyState: datasetsError ? (
        <span className="text-red-600">Failed to load datasets.</span>
      ) : isLoadingDatasets ? (
        "Loading datasets..."
      ) : (
        <Link
          to="/import/datasets/new"
          className="
            font-medium text-emerald-600
            hover:underline
          "
        >
          + Add a dataset
        </Link>
      ),
    },
  ];

  return (
    <div className="mt-6">
      <PipelineCards cards={cards} />

      <ProviderCard providerId={source.provider_id} />

      <DetailCardSection title="Details">
        <DetailList>
          <DetailRow label="ID" value={<IdValue id={source.id} />} />
          <DetailRow label="Name" value={source.name} />
          <DetailRow label="Type" value={vendorLabel} />
          <DetailRow label="Created at" value={formatDateTime(source.created_at)} />
          <DetailRow label="Updated at" value={formatDateTime(source.updated_at)} />
        </DetailList>
      </DetailCardSection>

      <DetailCardSection title={vendorLabel}>
        {configForm.isLoading ? (
          <Spinner />
        ) : configForm.error ? (
          <Alert kind={AlertKind.ERROR}>Failed to load source configuration.</Alert>
        ) : (
          <FormFieldDetails fields={configForm.fields} values={configForm.draft} />
        )}
      </DetailCardSection>
    </div>
  );
};

export default SourceOverview;
